import { useEffect } from 'react';
import Layout from './Layout/Layout';
import { ToastContainer, cssTransition } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCurrentUser, fetchCurrentUserConversations, fetchCurrentUserNotifications } from './features/auth/authThunks';
import { AppDispatch, RootState } from './store/store';
import { clearOAuthTokenFromUrl, getApiBaseUrl } from './features/auth/oauth';
import { hasAuthClientSession } from './features/auth/tokenCookie';
import {
  addBookingValidatedNotification,
  addIncomingChatMessage,
  applyBookingBookedDelta,
  applyBookingDatesUpdated,
  applyChatMessageDeleted,
  applyChatReadReceipt,
  applyChatTypingSignal,
  touchBookingStreamTick,
} from './features/auth/authSlice';
import { fetchAdDetailsById } from './features/adDetails/adDetailsThunks';
import { useTranslation } from 'react-i18next';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { useMemo } from 'react';
import { buildAppTheme } from './theme/appTheme';
import { trackSiteVisitApi } from './api/analyticsApi';

const VISIT_TRACKING_KEY = 'site_visit_tracked_at';
const VISIT_TRACKING_TTL_MS = 6 * 60 * 60 * 1000;
const topCenterToastTransition = cssTransition({
  enter: 'toast-slide-top-enter',
  exit: 'toast-slide-top-exit',
  collapse: true,
  collapseDuration: 220,
});

function App() {
  const { i18n } = useTranslation();
  const theme = useMemo(() => buildAppTheme(i18n.resolvedLanguage || 'fr'), [i18n.resolvedLanguage]);

  const dispatch = useDispatch<AppDispatch>();
  const currentAdId = useSelector((state: RootState) => {
    const item = state.adDetails?.item as any;
    return item?.id || item?._id || null;
  });
  const currentUserId = useSelector((state: RootState) => {
    const authUser = state.auth?.user as any;
    return authUser?.id || authUser?._id || null;
  });

  useEffect(() => {
    clearOAuthTokenFromUrl();
    if (hasAuthClientSession()) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const now = Date.now();
    const lastTrackedRaw = window.localStorage.getItem(VISIT_TRACKING_KEY);
    const lastTracked = Number(lastTrackedRaw || 0);
    if (Number.isFinite(lastTracked) && lastTracked > 0 && now - lastTracked < VISIT_TRACKING_TTL_MS) {
      return;
    }

    trackSiteVisitApi()
      .then(() => {
        window.localStorage.setItem(VISIT_TRACKING_KEY, String(now));
      })
      .catch(() => {
        // silent
      });
  }, []);

  useEffect(() => {
    if (!currentUserId) return;
    dispatch(fetchCurrentUserNotifications());
    dispatch(fetchCurrentUserConversations());
  }, [currentUserId, dispatch]);

  useEffect(() => {
    if (!currentUserId) return;
    let eventSource: EventSource | null = null;
    const baseUrl = getApiBaseUrl();
    const sseCandidates = [
      `${baseUrl}/currentUser/stream`,
      `${baseUrl}/stream`,
      `${baseUrl}/api/currentUser/stream`,
      `${baseUrl}/api/stream`,
    ];
    let currentCandidateIndex = 0;

    const openStream = (index: number) => {
      if (eventSource) eventSource.close();
      currentCandidateIndex = index;
      const streamUrl = sseCandidates[index];
      console.info('[SSE] connecting to', streamUrl);
      eventSource = new EventSource(streamUrl, { withCredentials: true });

      eventSource.addEventListener('booking_dates_updated', (event: MessageEvent) => {
        handleEventPayload(event.data);
      });
      eventSource.addEventListener('ad_booking_updated', (event: MessageEvent) => {
        handleEventPayload(event.data);
      });
      eventSource.addEventListener('chat_message', (event: MessageEvent) => {
        handleEventPayload(event.data);
      });
      eventSource.addEventListener('chat_read', (event: MessageEvent) => {
        handleEventPayload(event.data);
      });
      eventSource.addEventListener('chat_message_deleted', (event: MessageEvent) => {
        handleEventPayload(event.data);
      });
      eventSource.addEventListener('chat_typing', (event: MessageEvent) => {
        handleEventPayload(event.data);
      });

      eventSource.onmessage = (event) => {
        handleEventPayload(event.data);
      };

      eventSource.onerror = () => {
        const nextIndex = currentCandidateIndex + 1;
        if (nextIndex < sseCandidates.length) {
          console.warn('[SSE] failed, trying fallback route', sseCandidates[nextIndex]);
          openStream(nextIndex);
        } else {
          console.error('[SSE] all stream routes failed');
        }
      };
    };

    const handleEventPayload = (rawData: string) => {
      try {
        const payload = JSON.parse(rawData || '{}');
        const type = payload?.type;

        const pushRealtimeNotification = (notificationInput: any = {}) => {
          const ownerName = typeof notificationInput?.ownerName === 'string' ? notificationInput.ownerName : undefined;
          const adTitle = typeof notificationInput?.adTitle === 'string' ? notificationInput.adTitle : undefined;
          const adsId = typeof notificationInput?.adsId === 'string'
            ? notificationInput.adsId
            : (typeof payload?.adsId === 'string' ? payload.adsId : undefined);
          const validatedDate = typeof notificationInput?.validatedDate === 'string' ? notificationInput.validatedDate : undefined;
          const reservationDates = Array.isArray(notificationInput?.reservationDates) ? notificationInput.reservationDates : undefined;
          const id = typeof notificationInput?.id === 'string' ? notificationInput.id : undefined;
          const notificationType = typeof notificationInput?.type === 'string' ? notificationInput.type : undefined;
          const chatTargetUserId = typeof notificationInput?.chatTargetUserId === 'string' ? notificationInput.chatTargetUserId : undefined;
          const reportId = typeof notificationInput?.reportId === 'string' ? notificationInput.reportId : undefined;
          const reportMessage = typeof notificationInput?.reportMessage === 'string' ? notificationInput.reportMessage : undefined;
          const reporterName = typeof notificationInput?.reporterName === 'string' ? notificationInput.reporterName : undefined;
          const reporterEmail = typeof notificationInput?.reporterEmail === 'string' ? notificationInput.reporterEmail : undefined;
          const createdAt = typeof notificationInput?.createdAt === 'string' ? notificationInput.createdAt : new Date().toISOString();

          dispatch(addBookingValidatedNotification({
            count: 1,
            notification: {
              id,
              type: notificationType,
              ownerName,
              adTitle,
              adsId,
              validatedDate,
              reservationDates,
              chatTargetUserId,
              reportId,
              reportMessage,
              reporterName,
              reporterEmail,
              createdAt,
              read: false,
            },
          }));
        };

        if (type === 'booking_dates_updated') {
          dispatch(touchBookingStreamTick());
          dispatch(applyBookingDatesUpdated(payload));
          const payloadAdsId = payload?.adsId ? String(payload.adsId) : null;
          if (currentAdId && payloadAdsId && payloadAdsId === String(currentAdId)) {
            dispatch(fetchAdDetailsById(String(currentAdId)));
          }
          const reason = typeof payload?.reason === 'string' ? payload.reason : '';
          if (reason === 'validateBookingByDate' && payload?.notification && typeof payload.notification === 'object') {
            pushRealtimeNotification(payload.notification);
          }
          if (reason === 'cancelBooking' && payload?.notification && typeof payload.notification === 'object') {
            pushRealtimeNotification(payload.notification);
          }
          if (reason === 'saveBookingByAds' && payload?.notification && typeof payload.notification === 'object') {
            pushRealtimeNotification(payload.notification);
          }
          if (reason === 'newAdCreated' && payload?.notification && typeof payload.notification === 'object') {
            pushRealtimeNotification(payload.notification);
          }
          if (reason === 'adPendingValidation' && payload?.notification && typeof payload.notification === 'object') {
            pushRealtimeNotification(payload.notification);
          }
          if (reason === 'adValidated' && payload?.notification && typeof payload.notification === 'object') {
            pushRealtimeNotification(payload.notification);
          }
          if (reason === 'ownerReported' && payload?.notification && typeof payload.notification === 'object') {
            pushRealtimeNotification(payload.notification);
          }
          return;
        }

        if (type === 'ad_booking_updated') {
          dispatch(touchBookingStreamTick());
          const payloadAdsId = payload?.adsId ? String(payload.adsId) : null;
          if (currentAdId && payloadAdsId && payloadAdsId === String(currentAdId)) {
            dispatch(fetchAdDetailsById(String(currentAdId)));
          }
          return;
        }

        if (type === 'booking_booked_changed') {
          dispatch(touchBookingStreamTick());
          dispatch(applyBookingBookedDelta(payload));
          return;
        }

        if (type === 'chat_message') {
          dispatch(addIncomingChatMessage({
            message: payload?.message,
            interlocutor: payload?.interlocutor,
          }));
          return;
        }

        if (type === 'chat_read') {
          dispatch(applyChatReadReceipt({
            interlocutorId: payload?.interlocutorId,
            readAt: payload?.readAt,
          }));
          return;
        }

        if (type === 'chat_typing') {
          dispatch(applyChatTypingSignal({
            interlocutorId: payload?.interlocutorId,
            isTyping: payload?.isTyping,
            at: payload?.at,
          }));
          return;
        }

        if (type === 'chat_message_deleted') {
          dispatch(applyChatMessageDeleted({
            interlocutorId: payload?.interlocutorId,
            messageId: payload?.messageId,
            at: payload?.at,
          }));
        }
      } catch (_error) {
        // noop: ignore malformed events.
      }
    };

    openStream(0);

    return () => {
      if (eventSource) eventSource.close();
    };
  }, [currentAdId, currentUserId, dispatch]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        <Layout />
        <ToastContainer
          rtl={i18n.dir() === 'rtl'}
          autoClose={6000}
          position="top-center"
          transition={topCenterToastTransition}
          newestOnTop
          closeOnClick
          draggable={false}
          className="app-toast-container"
          toastClassName="app-toast"
        />
      </div>
    </ThemeProvider>
  );
}

export default App;
